(() => {
  'use strict';

  if (customElements.get('apella-cart-gift')) return;
  const celebratedCampaigns = new Set();
  const rupees = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });

  function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }

  function safeImage(value) {
    if (typeof value !== 'string' || !value || /[\\\u0000-\u0020\u007f]/.test(value)) return '';
    if (value.startsWith('/') && !value.startsWith('//')) return value;
    try {
      // Liquid image_url can return a scheme-relative Shopify CDN URL.
      // Canonicalize before applying the same authority checks as absolute URLs.
      const url = new URL(value.startsWith('//') ? `https:${value}` : value);
      return url.protocol === 'https:' && url.hostname === 'cdn.shopify.com' && !url.username && !url.password && !url.port ? url.href : '';
    } catch (_) { return ''; }
  }

  function imageSource(image, source) {
    const safe = safeImage(source);
    if (safe) {
      if (image.getAttribute('src') !== safe) image.setAttribute('src', safe);
      image.hidden = false;
    } else {
      image.removeAttribute('src');
      image.hidden = true;
    }
  }

  class ApellaCartGift extends HTMLElement {
    constructor() {
      super();
      this._bindRuntime = this._bindRuntime.bind(this);
    }

    connectedCallback() {
      if (!this._ui) this._build();
      document.addEventListener('apella:gift-ready', this._bindRuntime);
      document.addEventListener('DOMContentLoaded', this._bindRuntime);
      this._bindRuntime();
    }

    disconnectedCallback() {
      document.removeEventListener('apella:gift-ready', this._bindRuntime);
      document.removeEventListener('DOMContentLoaded', this._bindRuntime);
      if (this._unsubscribe) this._unsubscribe();
      this._unsubscribe = null;
      this._runtime = null;
      clearTimeout(this._celebrationTimer);
      this.classList.remove('is-celebrating');
    }

    _bindRuntime() {
      const runtime = window.ApellaShagun;
      if (!this.isConnected || !runtime || this._runtime === runtime || typeof runtime.subscribe !== 'function') return;
      if (this._unsubscribe) this._unsubscribe();
      this._runtime = runtime;
      this._unsubscribe = runtime.subscribe((state) => this._render(state));
    }

    _build() {
      const card = element('div', 'apella-cart-gift__card');
      const header = element('div', 'apella-cart-gift__header');
      const emblem = element('span', 'apella-cart-gift__emblem', '✉');
      emblem.setAttribute('aria-hidden', 'true');
      const intro = element('div', 'apella-cart-gift__intro');
      const eyebrow = element('span', 'apella-cart-gift__eyebrow', 'A little joy, from Apella');
      const heading = element('h3', 'apella-cart-gift__heading');
      const message = element('p', 'apella-cart-gift__message');
      intro.append(eyebrow, heading, message);
      header.append(emblem, intro);
      const progress = element('progress', 'apella-cart-gift__progress');
      progress.setAttribute('max', '100');
      progress.setAttribute('aria-label', 'Progress towards your Shagun envelope');
      const selected = element('div', 'apella-cart-gift__selected');
      const thumb = element('img', 'apella-cart-gift__thumb');
      thumb.setAttribute('width', '52');
      thumb.setAttribute('height', '64');
      thumb.setAttribute('alt', '');
      const selectionText = element('div', 'apella-cart-gift__selection-text');
      const title = element('span', 'apella-cart-gift__title');
      const designNote = element('span', 'apella-cart-gift__design-note');
      const included = element('span', 'apella-cart-gift__included');
      selectionText.append(title, designNote, included);
      selected.append(thumb, selectionText);
      const actions = element('div', 'apella-cart-gift__actions');
      const primary = element('button', 'apella-cart-gift__button');
      primary.setAttribute('type', 'button');
      const secondary = element('button', 'apella-cart-gift__text-button');
      secondary.setAttribute('type', 'button');
      primary.addEventListener('click', () => this._act(this._primaryAction));
      secondary.addEventListener('click', () => this._act(this._secondaryAction));
      actions.append(primary, secondary);
      const terms = element('p', 'apella-cart-gift__terms', 'On orders above ₹5,000. Cannot be combined with other offers.');
      const announcement = element('span', 'apella-cart-gift__announcement');
      announcement.setAttribute('role', 'status');
      announcement.setAttribute('aria-live', 'polite');
      announcement.setAttribute('aria-atomic', 'true');
      card.append(header, progress, selected, actions, terms, announcement);
      this.replaceChildren(card);
      this._ui = { heading, message, progress, selected, thumb, title, designNote, included, primary, secondary, actions, terms, announcement };
    }

    _hasManagedGift(state) {
      const config = state.config;
      const known = new Set((config.gifts || []).map((gift) => String(gift.variantId)));
      return (state.cart?.items || []).some((line) => known.has(String(line.variant_id ?? line.id)) && line.properties?.[config.markerKey] === config.campaignId);
    }

    _render(state) {
      this._state = state;
      if (!state?.config) { this.hidden = true; return; }
      const ui = this._ui;
      const managed = this._hasManagedGift(state);
      const inactive = state.status === 'DISABLED' || state.status === 'UNAVAILABLE' || !state.config.enabled;
      this.hidden = inactive && !managed;
      if (this.hidden) return;
      const status = inactive ? 'NEEDS_RECOVERY' : state.status;
      this.setAttribute('data-status', status);
      this.setAttribute('aria-busy', state.busy ? 'true' : 'false');
      let heading = 'Getting your Shagun ready…';
      let message = 'We’re checking your order. Just a moment.';
      let primary = '';
      let secondary = '';
      this._primaryAction = '';
      this._secondaryAction = '';
      switch (status) {
        case 'LOCKED': {
          const remaining = Number.isSafeInteger(state.remainingRupees) && state.remainingRupees > 0 ? `₹${rupees.format(state.remainingRupees)} away from your Shagun` : 'A Shagun envelope, with love';
          heading = remaining;
          message = 'A Shagun envelope is added automatically when you shop above ₹5,000.';
          break;
        }
        case 'CONFIRMED':
          heading = 'Your Shagun is FREE';
          message = 'A little extra joy, tucked into your order.';
          secondary = 'Remove gift';
          this._secondaryAction = 'remove';
          break;
        case 'OPTED_OUT':
          heading = 'Your Shagun is set aside';
          message = 'Changed your mind? You can add it again.';
          primary = 'Add gift again';
          this._primaryAction = 'restore';
          break;
        case 'CONFLICT':
          heading = 'Choose your preferred offer';
          message = 'Shagun cannot be combined with your other offer. You can keep that offer and continue without the gift.';
          primary = managed ? 'Remove gift & keep my offer' : 'Keep my offer';
          this._primaryAction = 'remove';
          secondary = 'Try gift again';
          this._secondaryAction = 'restore';
          break;
        case 'NEEDS_RECOVERY':
          heading = 'Let’s check your Shagun';
          message = inactive ? 'This gift is no longer available. Please remove it to continue.' : 'We couldn’t finish updating your gift. Try again or remove it to continue.';
          primary = inactive ? 'Remove gift' : 'Try again';
          this._primaryAction = inactive ? 'remove' : 'retry';
          secondary = inactive ? '' : 'Remove gift';
          this._secondaryAction = 'remove';
          break;
      }
      ui.heading.textContent = heading;
      ui.message.textContent = message;
      const showProgress = status === 'LOCKED';
      ui.progress.hidden = !showProgress;
      const net = Number.isSafeInteger(state.netPaise) && state.netPaise > 0 ? state.netPaise : 0;
      const threshold = Number.isSafeInteger(state.config.thresholdPaise) && state.config.thresholdPaise > 0 ? state.config.thresholdPaise : 500000;
      ui.progress.setAttribute('value', String(Math.min(100, Math.max(0, net / (threshold + 1) * 100))));
      ui.progress.setAttribute('aria-valuetext', showProgress ? heading : '');
      ui.selected.hidden = status !== 'CONFIRMED';
      // A selected entry does not prove a zero-price line. Only CONFIRMED may
      // expose the image, included price, and celebratory copy.
      ui.title.textContent = status === 'CONFIRMED' ? (state.gift?.title || 'Shagun Envelop') : '';
      ui.designNote.textContent = status === 'CONFIRMED' ? 'Design selected by our team' : '';
      ui.included.textContent = status === 'CONFIRMED' ? 'Included at ₹0' : '';
      imageSource(ui.thumb, status === 'CONFIRMED' ? state.gift?.image : '');
      ui.primary.textContent = primary;
      ui.primary.hidden = !primary;
      ui.primary.disabled = !!state.busy;
      ui.secondary.textContent = secondary;
      ui.secondary.hidden = !secondary;
      ui.secondary.disabled = !!state.busy;
      ui.actions.hidden = !primary && !secondary;
      ui.terms.hidden = inactive;
      const announcement = `${heading}. ${message}`;
      if (ui.announcement.textContent !== announcement) ui.announcement.textContent = announcement;
      this._celebrate(state, status);
    }

    _act(action) {
      if (!this._runtime || this._state?.busy) return;
      let result;
      if (action === 'remove') result = this._runtime.setOptOut(true);
      if (action === 'restore') result = this._runtime.setOptOut(false);
      if (action === 'retry') result = this._runtime.reconcile('ui-retry');
      // Runtime owns recovery state and all cart writes. Avoid an unhandled
      // rejection without replacing its useful recovery controls or copy.
      if (result && typeof result.catch === 'function') result.catch(() => {});
    }

    _celebrate(state, status) {
      if (status !== 'CONFIRMED') this.classList.remove('is-celebrating');
      if (status !== 'CONFIRMED' || !state.celebrate || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
      const campaign = state.config.campaignId;
      const drawer = this.closest('cart-drawer');
      if (celebratedCampaigns.has(campaign) || (drawer && !drawer.classList.contains('active'))) return;
      celebratedCampaigns.add(campaign);
      this.classList.add('is-celebrating');
      clearTimeout(this._celebrationTimer);
      this._celebrationTimer = setTimeout(() => this.classList.remove('is-celebrating'), 1100);
    }
  }

  customElements.define('apella-cart-gift', ApellaCartGift);
})();
